import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { endpoint } from "../../utils/APIRoutes";

const tabs = [
  { key: "account", label: "Account" },
  { key: "balance", label: "Balance" },
  { key: "positions", label: "Open Posit" },
  { key: "pending", label: "Pending Order" },
  { key: "chart", label: "Live Chart" },
];

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return 0;
  }

  const parsedValue = Number(value);
  return Number.isNaN(parsedValue) ? 0 : parsedValue;
};

const normalizeMt5Data = (payload) => ({
  account: payload?.a ?? "-",
  balance: toNumber(payload?.b),
  equity: toNumber(payload?.e),
  profit: toNumber(payload?.p),
  todayProfit: toNumber(payload?.tp),
  positions: Array.isArray(payload?.pos) ? payload.pos : [],
  pendingOrders: Array.isArray(payload?.ord) ? payload.ord : [],
});

const MT5TerminalLayout = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const account = params.get("account");
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState("account");

  useEffect(() => {
    const fetchData = async () => {
      const response = await axios.get(`${endpoint?.mt5_get_data}?account=${account}`);
      setData(normalizeMt5Data(response.data?.received));
    };

    fetchData();
    const intervalId = setInterval(fetchData, 60000);

    return () => clearInterval(intervalId);
  }, [account]);

  const renderMobileTab = (tabKey) => {
    switch (tabKey) {
      case "account":
        return (
          <Box title="Account" titleRight="OnePlus">
            <Info label="MT5 Account" value={data.account} />
          </Box>
        );
      case "balance":
        return (
          <Box title="Balance">
            <Info label="Balance" value={data.balance} />
            <Info label="Equity" value={data.equity} />
            <Info
              label="Current Profit"
              value={data.profit}
              valueClassName={data.profit >= 0 ? "text-[#48d279]" : "text-red-400"}
            />
            <Info
              label="Today Profit"
              value={data.todayProfit}
              valueClassName={data.todayProfit >= 0 ? "text-[#48d279]" : "text-red-400"}
            />
          </Box>
        );
      case "positions":
        return (
          <Box title="Open Positions" scroll>
            {data.positions.length === 0 ? (
              <Empty />
            ) : (
              <MiniTable headers={["Symbol", "Type", "Vol", "P/L"]}>
                {data.positions.map((position, index) => (
                  <tr key={position.ticket ?? `${position.symbol}-${index}`}>
                    <td>{position.symbol ?? "-"}</td>
                    <td>{position.type ?? "-"}</td>
                    <td>{position.volume ?? "-"}</td>
                    <td
                      className={
                        toNumber(position.profit) >= 0 ? "text-[#48d279]" : "text-red-400"
                      }
                    >
                      {position.profit ?? 0}
                    </td>
                  </tr>
                ))}
              </MiniTable>
            )}
          </Box>
        );
      case "pending":
        return (
          <Box title="Pending Order" scroll>
            {data.pendingOrders.length === 0 ? (
              <Empty />
            ) : (
              <MiniTable headers={["Symbol", "Type", "Vol", "Price"]}>
                {data.pendingOrders.map((order, index) => (
                  <tr key={order.ticket ?? `${order.symbol}-${index}`}>
                    <td>{order.symbol ?? "-"}</td>
                    <td>{order.type ?? "-"}</td>
                    <td>{order.volume ?? "-"}</td>
                    <td>{order.price ?? "-"}</td>
                  </tr>
                ))}
              </MiniTable>
            )}
          </Box>
        );
      case "chart":
        return (
          <Box title="Live Chart">
            <div className="h-96">
              <TradingViewChart symbol="OANDA:XAUUSD" interval="60" id="tradingview_mobile_xauusd" />
            </div>
          </Box>
        );
      default:
        return null;
    }
  };

  if (!data) {
    return (
      <div className="h-screen bg-black flex items-center justify-center text-white">
        Loading MT5 Data...
      </div>
    );
  }

  return (
    <div className="h-screen bg-[#0b0f14] text-gray-200 p-3 overflow-scroll">
      <div className="mb-3">
        <button
          type="button"
          onClick={() => navigate("/dashboard")}
          className="inline-flex items-center gap-2 rounded-lg border border-[#48d279]/50 bg-[#48d279]/10 px-3 py-1.5 text-sm font-medium text-[#48d279] hover:bg-[#48d279]/20"
        >
          <span aria-hidden="true">←</span>
          Back
        </button>
      </div>

      <div className="hidden md:grid grid-cols-12 gap-3 h-full">
        <Box col="col-span-4" title="Account" titleRight="OnePlus">
          <Info label="Account" value={data.account} />
        </Box>

        <Box col="col-span-8" title="Balance">
          <Info label="Balance" value={data.balance} />
          <Info label="Equity" value={data.equity} />
          <Info
            label="Current Profit"
            value={data.profit}
            valueClassName={data.profit >= 0 ? "text-[#48d279]" : "text-red-400"}
          />
          <Info
            label="Today Profit"
            value={data.todayProfit}
            valueClassName={data.todayProfit >= 0 ? "text-[#48d279]" : "text-red-400"}
          />
        </Box>

        <Box col="col-span-6 min-h-[420px]" title="Open Positions" scroll>
          {data.positions.length === 0 ? (
            <Empty />
          ) : (
            <MiniTable headers={["Symbol", "Type", "Vol", "P/L"]}>
              {data.positions.map((position, index) => (
                <tr key={position.ticket ?? `${position.symbol}-${index}`}>
                  <td>{position.symbol ?? "-"}</td>
                  <td>{position.type ?? "-"}</td>
                  <td>{position.volume ?? "-"}</td>
                  <td
                    className={
                      toNumber(position.profit) >= 0 ? "text-[#48d279]" : "text-red-400"
                    }
                  >
                    {position.profit ?? 0}
                  </td>
                </tr>
              ))}
            </MiniTable>
          )}
        </Box>

        <Box col="col-span-6 min-h-[420px]" title="Pending Order" scroll>
          {data.pendingOrders.length === 0 ? (
            <Empty />
          ) : (
            <MiniTable headers={["Symbol", "Type", "Vol", "Price"]}>
              {data.pendingOrders.map((order, index) => (
                <tr key={order.ticket ?? `${order.symbol}-${index}`}>
                  <td>{order.symbol ?? "-"}</td>
                  <td>{order.type ?? "-"}</td>
                  <td>{order.volume ?? "-"}</td>
                  <td>{order.price ?? "-"}</td>
                </tr>
              ))}
            </MiniTable>
          )}
        </Box>

        <Box col="col-span-12" title="Live Chart">
          <div className="h-[420px]">
            <TradingViewChart symbol="OANDA:XAUUSD" interval="60" id="tradingview_desktop_xauusd" />
          </div>
        </Box>
      </div>

      <div className="md:hidden">{renderMobileTab(activeTab)}</div>

      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-[#111827] border-t border-gray-700 z-50 mb-[30px] ">
        <div className="flex overflow-x-auto no-scrollbar py-2 px-1">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`
                flex-shrink-0 flex flex-col items-center justify-center text-xs font-medium px-3 py-2 m-1 rounded-lg transition-all duration-200
                ${
                  activeTab === tab.key
                    ? "bg-[#48d279] text-[#07110b] shadow-lg shadow-[#48d279]/30 transform scale-105"
                    : "text-gray-400 hover:text-[#48d279] hover:bg-[#48d279]/10"
                }
              `}
            >
              <span className="text-sm">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const Box = ({ title, titleRight, children, col, scroll }) => (
  <div
    className={`bg-[#111827] rounded-xl p-3 ${col ?? ""} ${scroll ? "overflow-y-auto" : ""}`}
  >
    <div className="mb-2 flex items-center justify-between">
      <h3 className="text-sm font-semibold text-gray-300">{title}</h3>
      {titleRight ? <span className="text-sm font-semibold text-[#48d279]">{titleRight}</span> : null}
    </div>
    {children}
  </div>
);

const Info = ({ label, value, valueClassName = "" }) => (
  <div className="flex justify-between text-sm">
    <span className="text-gray-400">{label}</span>
    <span className={valueClassName}>{value}</span>
  </div>
);

const MiniTable = ({ headers, children }) => (
  <table className="w-full text-xs">
    <thead>
      <tr className="text-gray-400 border-b border-gray-700">
        {headers.map((header) => (
          <th key={header} className="text-left py-1">
            {header}
          </th>
        ))}
      </tr>
    </thead>
    <tbody className="divide-y divide-gray-800">{children}</tbody>
  </table>
);

const Empty = () => <div className="text-gray-500 text-sm">No data</div>;

const TradingViewChart = ({ symbol, interval, id }) => {
  const container = useRef(null);

  useEffect(() => {
    if (!container.current) {
      return;
    }

    container.current.innerHTML = "";

    const script = document.createElement("script");
    script.src = "https://s3.tradingview.com/tv.js";
    script.async = true;

    script.onload = () => {
      if (!window.TradingView) {
        return;
      }

      new window.TradingView.widget({
        autosize: true,
        symbol,
        interval,
        timezone: "Etc/UTC",
        theme: "dark",
        style: "1",
        locale: "en",
        toolbar_bg: "#0b0f14",
        enable_publishing: false,
        hide_side_toolbar: false,
        allow_symbol_change: false,
        container_id: id,
      });
    };

    container.current.appendChild(script);
  }, [symbol, interval, id]);

  return <div ref={container} id={id} className="w-full h-full" />;
};

export default MT5TerminalLayout;
